import { useEffect, useState } from 'react';
import type { AuthProviders } from '../../shared/types';
import { api } from '../lib/api';
import { useQuery } from '../lib/router';
import { Icon } from '../components/Icon';
import { Alert, Button, Field } from '../components/ui';

const ERRORS: Record<string, string> = {
  not_member: 'You need to be a member of the Ravenbound Discord server to create a dashboard account. Join the server, then sign in again.',
  left_guild: 'You are no longer a member of the Ravenbound Discord server, so your dashboard account is locked. You’ll get access back as soon as you rejoin and your membership syncs.',
  suspended: 'Your dashboard access has been suspended. Contact an admin.',
  inactive: 'Your account is inactive. Contact an admin.',
  discord_failed: 'Discord sign-in failed. Please try again.',
  google_failed: 'Google sign-in failed. Please try again.',
  google_not_linked: 'That Google account is not linked to a dashboard account. Sign in with Discord first, then link Google from your profile.',
  cancelled: 'Sign-in was cancelled.',
};

export function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const q = useQuery();
  const [providers, setProviders] = useState<AuthProviders | null>(null);
  const [devUsers, setDevUsers] = useState<{ id: string; name: string }[]>([]);
  const [mode, setMode] = useState<'main' | 'password' | 'setup'>('main');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(ERRORS[q.get('error') ?? ''] ?? null);
  const [busy, setBusy] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    api.get<AuthProviders>('/api/auth/providers').then((p) => {
      setProviders(p);
      if (p.devLogin) api.get('/api/auth/dev/users').then(setDevUsers).catch(() => {});
    });
    api.get('/api/auth/breakglass/status').then((s) => setNeedsSetup(s.needsSetup && s.setupAvailable)).catch(() => {});
  }, []);

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post('/api/auth/password/login', { login, password });
      onSignedIn();
    } catch (ex: any) {
      if (ex.code === 'setup_required') setMode('setup');
      setErr(ex.message);
    } finally { setBusy(false); }
  };

  const submitSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== password2) return setErr('Passwords do not match');
    setBusy(true); setErr(null);
    try {
      await api.post('/api/auth/breakglass/setup', { code, password });
      onSignedIn();
    } catch (ex: any) { setErr(ex.message); } finally { setBusy(false); }
  };

  return (
    <div className="login">
      <div className="login-art" aria-hidden="true">
        <div className="login-glow" />
      </div>
      <div className="login-card">
        <img src="/logo.png" alt="Ravenbound emblem" width={112} height={112} className="login-logo" />
        <div className="eyebrow">Ravenbound · RABD</div>
        <h1>Org Dashboard</h1>
        <p className="muted">Fleet command, divisions and tools for Ravenbound members.</p>

        {err && <Alert kind="danger">{err}</Alert>}

        {mode === 'main' && (
          <div className="login-actions">
            {providers?.discord && (
              <a className="btn btn-discord btn-lg" href="/api/auth/discord/start"><Icon name="discord" size={20} /> Sign in with Discord</a>
            )}
            {providers?.google && (
              <a className="btn btn-ghost btn-lg" href="/api/auth/google/start"><Icon name="google" size={18} /> Sign in with Google</a>
            )}
            <Button variant="ghost" className="btn-lg" icon="key" onClick={() => { setMode('password'); setErr(null); }}>Sign in with password</Button>

            {providers?.devLogin && (
              <div className="dev-login">
                <div className="divider"><span>Development: fake Discord server</span></div>
                <div className="dev-grid">
                  {devUsers.map((u) => <a key={u.id} className="btn btn-subtle" href={`/api/auth/dev/login?discordId=${u.id}`}>{u.name}</a>)}
                </div>
              </div>
            )}
            {needsSetup && (
              <button className="link-btn" onClick={() => { setMode('setup'); setErr(null); }}>First-time break-glass setup</button>
            )}
          </div>
        )}

        {mode === 'password' && (
          <form className="login-form" onSubmit={submitPassword}>
            <Field label="Login name"><input autoFocus autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} /></Field>
            <Field label="Password"><input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
            <Button variant="primary" className="btn-lg" busy={busy} type="submit">Sign in</Button>
            <button type="button" className="link-btn" onClick={() => setMode('main')}>Back</button>
          </form>
        )}

        {mode === 'setup' && (
          <form className="login-form" onSubmit={submitSetup}>
            <Alert kind="info">Break-glass account first sign-in. Enter the one-time setup code from your deployment secrets, then choose a password (12+ characters, letters and numbers). Login name: <b>breakglass</b>.</Alert>
            <Field label="Setup code"><input autoFocus value={code} onChange={(e) => setCode(e.target.value)} spellCheck={false} /></Field>
            <Field label="New password"><input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
            <Field label="Repeat password"><input type="password" autoComplete="new-password" value={password2} onChange={(e) => setPassword2(e.target.value)} /></Field>
            <Button variant="primary" className="btn-lg" busy={busy} type="submit">Set password and sign in</Button>
            <button type="button" className="link-btn" onClick={() => setMode('main')}>Back</button>
          </form>
        )}
        <div className="login-legal"><a href="/privacy.html">Privacy</a> · <a href="/terms.html">Terms</a></div>
      </div>
    </div>
  );
}
